import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, Button, IconButton, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { BASE_URL, fetchAsnDocumentsList, uploadASNDocuments } from "Service/ApiService";

import { CloudUpload, FactCheck, Inventory2, ReceiptLong, History, OpenInNew, Close, CheckCircleOutline } from "@mui/icons-material";

const documentIcons = { selfInspectionQualityChecklist: FactCheck, packingSlip: Inventory2, invoiceCopy: ReceiptLong };

const outlineAction = {
  color: "#175cd3", borderColor: "#b2ccff", backgroundColor: "#fff",
  textTransform: "none", fontWeight: 600,
  "&:hover": { color: "#1849a9", borderColor: "#175cd3", backgroundColor: "#eff6ff", opacity: 1 },
  "&.Mui-disabled": { color: "#667085", borderColor: "#d0d5dd", backgroundColor: "#f2f4f7" },
};

const fields = [
  ["selfInspectionQualityChecklist", "Self-Inspection Quality Checklist", "docment1Name"],
  ["packingSlip", "Packing Slip", "document2Name"],
  ["invoiceCopy", "Invoice Copy", "document3Name"],
];

export default function ASNDocumentsDialog({ row, onClose, onUploaded }) {
  const viewOnly = String(row.approve || "").trim().toLowerCase() === "pending";
  const [sets, setSets] = useState([]);
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [inputKey, setInputKey] = useState(0);

  const load = async () => {
    const result = await fetchAsnDocumentsList(row.vendorCode, row.poNumber);
    if (result?.success !== "true" && result?.msg !== "No Data Found!") {
      throw new Error(result?.msg || "Unable to load documents. Please retry.");
    }
    setSets(result.asnDocumentList || []);
  };

  useEffect(() => {
    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);

  const upload = async () => {
    if (viewOnly) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const result = await uploadASNDocuments(row.vendorCode, row.poNumber, files);
      if (result?.success !== "true") throw new Error(result?.msg || "Upload failed.");
      setFiles({});
      setInputKey((key) => key + 1);
      setSuccess("New document set uploaded. Previous documents are preserved.");
      await load();
      onUploaded();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}><ReceiptLong color="info" />Documents - PO {row.poNumber}</Box>
        <IconButton aria-label="Close documents" disabled={saving} onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f8fafc", p: { xs: 1, md: 1.5 }, color: "#344054" }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {!viewOnly && <Box sx={{ backgroundColor: "#fff", border: "1px solid #dce3ed", borderRadius: 2, p: 1 }}><Typography variant="h6" sx={{ color: "#182230" }}>Upload Additional Documents</Typography>
        <Typography variant="body2" sx={{ mb: 0.75, mt: 0.25, fontSize: 13, color: "#475467" }}>Select at least one document. PDF, JPG or PNG, up to 10 MB per file. Existing documents remain available.</Typography>
        <Box key={inputKey} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1, mb: 0.75 }}>
          {fields.map(([key, label]) => {
            const DocumentIcon = documentIcons[key];
            return (
            <Box key={key} sx={{ p: 1, border: "1px solid", borderColor: files[key] ? "#84adff" : "#dce3ed", borderRadius: 2, backgroundColor: files[key] ? "#eff6ff" : "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minHeight: 28 }}>
                <Box sx={{ display: "flex", p: 0.75, borderRadius: 1, backgroundColor: "#eaf2ff", color: "#175cd3" }}><DocumentIcon sx={{ fontSize: "22px !important" }} /></Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1d2939", lineHeight: 1.4 }}>{label}</Typography>
              </Box>
              <Button component="label" variant="outlined" color="info" size="small" startIcon={<CloudUpload />} disabled={saving} sx={{ ...outlineAction, alignSelf: "flex-start" }}>
                {files[key] ? "Change File" : "Choose File"}
              <input id={`asn-${key}`} type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={saving} aria-label={label} style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0, 0, 0, 0)" }} onChange={(event) => {
                const file = event.target.files?.[0];
                setError("");
                if (file && (!/\.(pdf|jpe?g|png)$/i.test(file.name) || file.size > 10 * 1024 * 1024 || file.size === 0)) {
                  setError("Choose a non-empty PDF, JPG or PNG file up to 10 MB.");
                  event.target.value = "";
                  setFiles((current) => ({ ...current, [key]: null }));
                  return;
                }
                setFiles((current) => ({ ...current, [key]: file }));
              }} />
              </Button>
              <Typography variant="caption" sx={{ wordBreak: "break-word", fontSize: 12, color: files[key] ? "#175cd3" : "#667085" }}>
                {files[key] ? files[key].name : "No file selected"}
              </Typography>
              {files[key] && <CheckCircleOutline color="success" fontSize="small" />}
            </Box>
          );})}
        </Box>
        <Button variant="contained" color="info" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />} sx={{ color: "#fff", backgroundColor: "#175cd3", boxShadow: "none", "&:hover": { backgroundColor: "#1849a9", boxShadow: "none" }, "&.Mui-disabled": { color: "#667085", backgroundColor: "#e4e7ec" } }} disabled={saving || loading || !Object.values(files).some(Boolean)} onClick={upload}>{saving ? "Uploading…" : "Upload New Set"}</Button>
        </Box>}<Typography variant="h6" sx={{ mt: viewOnly ? 0 : 1.5, mb: 0.75, color: "#182230", display: "flex", alignItems: "center", gap: 1 }}><History color="info" />Document History</Typography>
        {loading ? <CircularProgress size={24} /> : !sets.length ? <Typography variant="body2">No documents uploaded.</Typography> : sets.map((set) => (
          <Box key={set.asnDocId} sx={{ border: "1px solid #cbd5e1", borderRadius: 2, overflow: "hidden", backgroundColor: "#fff", mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", px: 2, py: 1.25, backgroundColor: "#edf2f8", borderBottom: "1px solid #dce3ed" }}>
              <Typography variant="subtitle2" sx={{ color: "#1d2939", fontWeight: 700 }}>Set #{set.asnDocId}</Typography>
              {set.createdDate && <Typography variant="caption" sx={{ color: "#475467" }}>Uploaded: {set.createdDate}</Typography>}

            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, p: 2 }}>
              {fields.filter(([, , field]) => set[field]).map(([key, label, field]) => {
                const DocumentIcon = documentIcons[key];
                return (
                  <Box key={field} component="a" href={`${BASE_URL}vendor/uploads/asn_documents/${encodeURIComponent(set[field])}`} target="_blank" rel="noopener noreferrer"
                    sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1.5, border: "1px solid #d0d5dd", borderRadius: 1.5, backgroundColor: "#fff", textDecoration: "none", transition: "background-color 0.15s", "&:hover": { backgroundColor: "#eff6ff", borderColor: "#84adff" }, "&:focus-visible": { outline: "2px solid #175cd3", outlineOffset: 2 } }}>
                    <Box sx={{ display: "flex", p: 1, borderRadius: 1, backgroundColor: "#eaf2ff", color: "#175cd3" }}><DocumentIcon sx={{ fontSize: "24px !important" }} /></Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: "#1d2939" }}>{label}</Typography>
                      <Typography sx={{ fontSize: 11, color: "#667085", mt: 0.5 }}>{String(set[field]).split(".").pop().toUpperCase()} document</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#175cd3", fontSize: 12, fontWeight: 700 }}>View <OpenInNew sx={{ fontSize: "16px !important" }} /></Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions><Button variant="outlined" sx={outlineAction} disabled={saving} onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

ASNDocumentsDialog.propTypes = {
  row: PropTypes.shape({ vendorCode: PropTypes.any, poNumber: PropTypes.any, approve: PropTypes.string }).isRequired,
  onClose: PropTypes.func.isRequired,
  onUploaded: PropTypes.func.isRequired,
};
